import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {APIClient} from '../../../../utils/APIClient';
import {API_URL} from '../../../../settings/local_settings';
import {Plus, X} from '../../../icons';
import {MOMENTOS, ROLES} from './PromptFilters';

/**
 * «Sube tu propio prompt» — el form inline del cierre del Recorrido.
 *
 * Es el mismo endpoint que el modal de la biblioteca clásica (`CreatePromptModal`), pero acá el
 * prompt nace **con scope de CLIENTE**: la banda vive dentro de la ficha de un cliente y lo que se
 * sube desde ahí es material de ese cliente, no de toda la agencia. El backend lo exige explícito
 * (`scope: 'CLIENT'` + `agency_client_id`); sin el id devuelve 400 y sin el scope el prompt se
 * publicaría para todos los clientes de la agencia sin que nadie lo haya pedido.
 *
 * ⚠️ La URL real es `agent/prompt-library/create/`, NO `agent/prompt-library/` — el POST cuelga de
 * una vista propia (`PromptLibraryCreateEndpoint`, `agent_tools/urls.py:98`); el path sin sufijo es
 * la lista y sólo responde GET.
 *
 * Al crearse, el prompt entra a la lista local (`onCreated`) sin volver a pedir la biblioteca: el
 * backend devuelve el `library_data` completo del prompt nuevo, que es exactamente el shape que
 * consumen el grid, los filtros y el ranking.
 */

export function UploadPromptForm({clientId, onCreated}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [promptText, setPromptText] = useState('');
    const [momento, setMomento] = useState('');
    const [role, setRole] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);

    const reset = useCallback(() => {
        setTitle('');
        setPromptText('');
        setMomento('');
        setRole('');
        setError(null);
    }, []);

    const toggle = useCallback(() => {
        setOpen((wasOpen) => {
            if (wasOpen) reset();
            return !wasOpen;
        });
        setSaved(false);
    }, [reset]);

    const handleSubmit = useCallback(() => {
        const cleanTitle = title.trim();
        const cleanText = promptText.trim();
        // Los dos campos obligatorios son los mismos que valida el backend. Se chequean acá también
        // para no gastar un round-trip en decir lo que ya se sabe.
        if (!cleanTitle || !cleanText) {
            setError('validation');
            return;
        }
        setSaving(true);
        setError(null);
        const payload = {
            title: cleanTitle,
            prompt_text: cleanText,
            role,
            momentos: momento ? [momento] : [],
            scope: 'CLIENT',
            agency_client_id: clientId,
        };
        new APIClient(`${API_URL}agent/prompt-library/create/`).call('POST', payload).then((data) => {
            setSaving(false);
            // `APIClient` nunca rechaza: un fallo llega como objeto con `status`. Y un 201 sin `id`
            // no es un prompt — sin ese guard entraría al state una fila que el grid no sabe pintar.
            if (!data || data.status === 'ERROR' || data.status === 'NETWORK_ERROR' || !data.id) {
                setError('failed');
                return;
            }
            reset();
            setOpen(false);
            setSaved(true);
            if (onCreated) onCreated(data);
        });
    }, [title, promptText, role, momento, clientId, onCreated, reset]);

    return (
        <div className="iab-addprompt">
            <button type="button" className="iab-ap-toggle" onClick={toggle} aria-expanded={open}>
                <span className="iab-ap-plus" aria-hidden="true">
                    {open ? <X size={12} weight="bold"/> : <Plus size={12} weight="bold"/>}
                </span>
                {t('ia_booster.band.upload_toggle')}
                <span className="iab-ap-hint">{t('ia_booster.band.upload_hint')}</span>
            </button>

            {saved && !open && <p className="iab-ap-ok">{t('ia_booster.band.upload_saved')}</p>}

            {open && (
                <div className="iab-ap-form">
                    <label className="iab-ap-label" htmlFor="iab-ap-title">
                        {t('ia_booster.band.upload_title_label')}
                    </label>
                    <input
                        id="iab-ap-title"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={t('ia_booster.band.upload_title_ph')}
                    />
                    <label className="iab-ap-label" htmlFor="iab-ap-text">
                        {t('ia_booster.band.upload_text_label')}
                    </label>
                    <textarea
                        id="iab-ap-text"
                        value={promptText}
                        onChange={(e) => setPromptText(e.target.value)}
                        placeholder={t('ia_booster.band.upload_text_ph')}
                    />
                    <div className="iab-ap-row">
                        <select
                            className="iab-bsel"
                            value={momento}
                            onChange={(e) => setMomento(e.target.value)}
                            aria-label={t('ia_booster.band.filter_momento')}
                        >
                            <option value="">{t('ia_booster.band.upload_momento_none')}</option>
                            {MOMENTOS.map((m) => (
                                <option key={m} value={m}>{t(`prompt_library.momentos.${m}`)}</option>
                            ))}
                        </select>
                        <select
                            className="iab-bsel"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            aria-label={t('ia_booster.band.filter_role')}
                        >
                            <option value="">{t('ia_booster.band.upload_role_none')}</option>
                            {ROLES.map((r) => (
                                <option key={r} value={r}>{t(`prompt_library.roles.${r}`)}</option>
                            ))}
                        </select>
                        <span className="iab-ap-spacer"/>
                        <button type="button" className="iab-btn -ghost" onClick={toggle}>
                            {t('ia_booster.band.upload_cancel')}
                        </button>
                        <button type="button" className="iab-btn -ia" onClick={handleSubmit} disabled={saving}>
                            {t(saving ? 'ia_booster.band.upload_saving' : 'ia_booster.band.upload_save')}
                        </button>
                    </div>
                    {error && (
                        <p className="iab-ap-error">
                            {t(error === 'validation'
                                ? 'ia_booster.band.upload_validation'
                                : 'ia_booster.band.upload_error')}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

export default UploadPromptForm;
